import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

// "מיקום לפי Search Console — 24 שעות אחרונות" — a separate table below the
// main keywords table. Every tracked keyword with Google's own average
// position (Israel, exact query) over the last 24 hours of GSC hourly data.
// TEMPORARY companion to the rank tracker while DataForSEO returns
// bot-degraded SERPs. Hourly data is preliminary, and a keyword with only a
// few impressions in 24h is a point reading, so impressions sit next to it.

function posClass(pos) {
  if (!pos) return 'beyond';
  if (pos <= 3) return 'top3';
  if (pos <= 10) return 'top10';
  if (pos <= 20) return 'top20';
  return 'beyond';
}

function fmtHour(s) {
  if (!s) return null;
  try {
    return new Date(s).toLocaleString('he-IL', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
  } catch (e) { return null; }
}

export default function GscKeywords24h({ siteId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('position');
  const [sortDir, setSortDir] = useState('asc');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.getGscKeywords24h(siteId));
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  function handleSort(field) {
    if (sortBy === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir(field === 'position' || field === 'keyword' ? 'asc' : 'desc'); }
  }

  var sectionTitle = React.createElement('div', {style:{display:'flex',alignItems:'center',gap:'10px',margin:'32px 0 14px',flexWrap:'wrap'}},
    React.createElement('h2', {style:{fontSize:'17px',fontWeight:700,color:'var(--text)',margin:0}}, 'מיקום לפי Search Console'),
    React.createElement('span', {style:{fontSize:'12px',color:'var(--text-muted)'}},
      '24 השעות האחרונות · חיפושים ב' + ((data && data.country_label) || 'ישראל') + (data && data.window_end ? ' · עד ' + fmtHour(data.window_end) : '')),
    React.createElement('div', {style:{flex:1,height:'1px',background:'var(--border)'}}),
    React.createElement('button', {className:'btn-sm', onClick:load, disabled:loading}, loading ? 'טוען...' : 'רענון')
  );

  if (loading && !data) return React.createElement(React.Fragment, null, sectionTitle, React.createElement('div', {className:'loading'}, 'טוען...'));
  if (error) return React.createElement(React.Fragment, null, sectionTitle,
    React.createElement('div', {className:'section-card', style:{padding:'16px',color:'var(--red)',fontSize:'13px'}}, 'לא ניתן לטעון נתוני Search Console: ' + error));

  var rows = (data && data.rows) || [];
  var withData = rows.filter(function(r) { return r.position != null; }).length;

  var sorted = rows.slice().sort(function(a, b) {
    var dir = sortDir === 'asc' ? 1 : -1;
    var va = a[sortBy], vb = b[sortBy];
    // Keywords without impressions always sink to the bottom.
    if (sortBy === 'position') {
      if (va == null && vb == null) return a.keyword.localeCompare(b.keyword, 'he');
      if (va == null) return 1;
      if (vb == null) return -1;
    }
    if (sortBy === 'keyword') return dir * a.keyword.localeCompare(b.keyword, 'he');
    return dir * ((va || 0) - (vb || 0));
  });

  var numCell = {textAlign:'center',fontFamily:'var(--mono)',fontSize:'13px',color:'var(--text-secondary)'};

  return React.createElement(React.Fragment, null,
    sectionTitle,
    React.createElement('div', {style:{fontSize:'12px',color:'var(--text-muted)',marginBottom:'10px'}},
      withData + ' מתוך ' + rows.length + ' ביטויים הופיעו בגוגל ב-24 השעות האחרונות. ' +
      'מיקום ממוצע לפי גוגל; הנתונים השעתיים ראשוניים ועשויים להתעדכן, ומיקום עם מעט חשיפות הוא מדידה נקודתית בלבד.'),
    React.createElement('div', {className:'section-card animate-in'},
      React.createElement('table', {className:'rankings-table'},
        React.createElement('thead', null,
          React.createElement('tr', null,
            [{label:'ביטוי', field:'keyword'}, {label:'מיקום', field:'position'}, {label:'חשיפות', field:'impressions'}, {label:'קליקים', field:'clicks'}].map(function(col) {
              return React.createElement('th', {key:col.field, onClick:function() { handleSort(col.field); },
                style:{cursor:'pointer', color: sortBy === col.field ? 'var(--accent)' : undefined}},
                col.label + ' ' + (sortBy === col.field ? (sortDir === 'asc' ? '▲' : '▼') : ''));
            })
          )
        ),
        React.createElement('tbody', null,
          sorted.map(function(r) {
            return React.createElement('tr', {key:r.keyword_id, style:{opacity: r.position == null ? 0.55 : 1}},
              React.createElement('td', null, React.createElement('span', {className:'keyword-text'}, r.keyword)),
              React.createElement('td', null, r.position == null
                ? React.createElement('span', {style:{color:'var(--text-muted)'}, title:'אין חשיפות ב-24 השעות האחרונות'}, '-')
                : React.createElement('span', {className:'position-badge ' + posClass(Math.round(r.position))}, r.position)),
              React.createElement('td', {style:numCell}, r.impressions ? r.impressions.toLocaleString() : '-'),
              React.createElement('td', {style:numCell}, r.clicks ? r.clicks.toLocaleString() : '-')
            );
          })
        )
      )
    )
  );
}
